import { useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './Popover.module.css';

/**
 * Anchored popover: closes on outside click and on Escape, and returns focus
 * to its trigger. Every dropdown, picker and menu on the page uses this so the
 * dismissal behaviour is identical rather than reimplemented per control.
 */
export function Popover({
  trigger,
  children,
  align = 'left',
  open: controlledOpen,
  onOpenChange,
}: {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  children: (props: { close: () => void }) => ReactNode;
  align?: 'left' | 'right';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlledOpen ?? uncontrolled;
  const setOpen = (v: boolean): void => {
    setUncontrolled(v);
    onOpenChange?.(v);
  };

  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      {trigger({ open, toggle: () => { setOpen(!open); } })}
      {open && (
        <div className={`${styles.panel} ${align === 'right' ? styles.right : styles.left}`}>
          {children({ close: () => { setOpen(false); } })}
        </div>
      )}
    </div>
  );
}
